import React, { useState, useEffect, useMemo } from "react";

import { InfoIcon as Info, Label as Text, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";

import ErrorBoundary from "Components/ErrorBoundary";
import {
  QUERY_PREVIOUS_PAGE,
  QUERY_PROJECT_CREATION,
  QUERY_PROJECT_BILLING,
  ACTIVITY_TYPES,
  ACTIVITY_CONTEXT,
  getResourceProfileLabel
} from "Constants/constants";
import { useNotification } from "Containers/Notifications";
import { Button, IconControl } from "ds/Button";
import * as Dialog from "ds/Dialog";
import Loading from "ds/PageLoading";
import * as Sticker from "ds/Sticker";
import { useActivityListener } from "Hooks/useActivityListener";
import useDecodedParams from "Hooks/useDecodedParams";
import useMediaQuery from "Hooks/useMediaQuery";
import useQueryParams from "Hooks/useQueryStrings";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { difference } from "Libs/difference";
import { setDeep } from "Libs/objectAccess";
import { getTrial, objectKeys, objectEntries, roundedNumber } from "Libs/utils";
import { environmentLoadingSelector } from "Reducers/environment";
import {
  loadDeploymentCurrent,
  loadDeploymentNext,
  nextDeploymentSelector,
  updateDeployment,
  isLoadingSelector,
  isUpdateLoadingSelector,
  nextDeploymentErrorSelector,
  deploymentStatusCodesSelector,
  resourcesDeploymentStatusSelector,
  resourcesAccumulatorHandler,
  resourcesFormatHandler
} from "Reducers/environment/deployment";
import {
  selectNonInactiveEnvironments,
  environmentStatusByDeploymentSelector
} from "Reducers/environment/environment.selectors";
import { selectFeatureAutoscalingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { organizationProfileSelector } from "Reducers/organization/profile";
import { gitProjectSelector } from "Reducers/project/project";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import Logo from "src/common/containers/NavBar/Logo/Logo";
import { checkTrialMessage } from "src/common/containers/NavBar/OrganizationsSwitcher/utils";
import ProjectError from "src/organization/pages/project/containers/ProjectError/ProjectError";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import { useAutoscallingValues } from "../metrics/containers/Service/util";

import InactiveEnvironmentWarning from "./components/InactiveEnvironmentWarning";
import ResourceRow from "./components/ResourceRow";
import * as R from "./components/ResourceRow/ResourceRow.style";
import SubscribeBanner from "./components/SubscribeBanner";
import Total from "./components/Total";
import * as S from "./ConfigureResourcesPage.style";
import { useProjectActivityManager } from "./hooks/useProjectActivityManager";

import type { DeploymentUpdateParams } from "Api/organizations/projects/environments/deployments/types";
import type {
  ResourceCollectionType,
  FlexibleResources,
  ModifiedFlexibleResources,
  ModifiedDeploymentService,
  TotalResources
} from "Reducers/environment/deployment";

type ResourceTypes = "webapps" | "workers" | "services";

type Change = "increased" | "reduced" | "";
type EnvironmentSelection = {
  value: string;
  label: string;
};

type LegacyFormStateMutation<T> = {
  [K in keyof T]: {
    [E in keyof T[K]]: T[K][E] & {
      resourceCollectionType: ResourceTypes;
    };
  };
};

type LegacyFormStateFlexibleResources =
  LegacyFormStateMutation<FlexibleResources>;

type LegacyFormStateModifiedFlexibleResources =
  LegacyFormStateMutation<ModifiedFlexibleResources>;

type FormState =
  | LegacyFormStateFlexibleResources
  | LegacyFormStateModifiedFlexibleResources;

const compareResource = (
  initValue: number | undefined,
  changedValue: number | undefined
): Change => {
  if (initValue === changedValue) return "";
  if (
    (typeof initValue !== "number" && typeof changedValue === "number") ||
    (typeof initValue === "number" &&
      typeof changedValue === "number" &&
      initValue < changedValue)
  )
    return "increased";
  return "reduced";
};

const ConfigureResourcesPage = () => {
  const lessThan720 = useMediaQuery(`(max-width: 720px)`);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const notify = useNotification();

  const autoscalingEnabledFlag = useAppSelector(
    selectFeatureAutoscalingEnabled
  );

  const { previousPage, projectCreation, projectBilling } = useQueryParams([
    "previousPage",
    "projectCreation",
    "projectBilling"
  ]);

  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [resourceErrors, setResourceErrors] =
    useState<
      Record<
        ResourceCollectionType,
        Record<string, { profileSize: boolean; disk: boolean }>
      >
    >();
  const [hasChangedResources, setHasChangedResources] = useState<Change>("");
  const [totalValues, setTotalValues] = useState<
    Omit<TotalResources, "status">
  >({
    cpu: 0,
    ram: 0,
    disk: 0,
    instances: 0
  });

  const [changedResourcesNext, setChangedResourcesNext] = useState<FormState>();
  const [initialResourcesNext, setInitialResourcesNext] = useState<FormState>();

  const changedResourcesNextLength = objectKeys(changedResourcesNext).length;

  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const organizationProfile = useSelectorWithUrlParams(
    organizationProfileSelector
  );
  const environments = useSelectorWithUrlParams(selectNonInactiveEnvironments);

  const [
    environmentOverviewUrl,
    buildResourcesSettingsUrl,
    organizationBillingUrl
  ] = useUrls([
    "organization.project.environment",
    "organization.project.settings.resources",
    "organization.billing"
  ]);

  const onExit = () => {
    navigate(previousPage!);
  };
  const environmentDropdownOptions: EnvironmentSelection[] = environments.map(
    env => ({
      value: env.id,
      label: env.title
    })
  );
  const defaultEnvironmentSelection = environmentDropdownOptions.find(
    env => env.value === environmentId
  ) ?? { value: "", label: "" };

  const isDeploymentLoading = useAppSelector(isLoadingSelector);
  const isDeploymentUpdatingLoading = useAppSelector(isUpdateLoadingSelector);
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector
  );
  const deploymentNextError = useSelectorWithUrlParams(
    nextDeploymentErrorSelector
  );
  const nextDeployment = useSelectorWithUrlParams(nextDeploymentSelector);
  const resourceStatus = useSelectorWithUrlParams(
    resourcesDeploymentStatusSelector
  );
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const { current: currentCode, next: nextCode } = useSelectorWithUrlParams(
    deploymentStatusCodesSelector
  );
  const isEnvironmentInactive = useSelectorWithUrlParams(
    environmentStatusByDeploymentSelector
  );

  const { hasCapability } = useAutoscallingValues();

  const isUpdateActivityInProgress = useActivityListener({
    watchForActivityTypes: [ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE],
    context: ACTIVITY_CONTEXT.Project
  });

  /**
   * When a user updates the resources, comes back to the configure resources page and reloads the page, there aren't any activities for useActivityListener to listen to.
   *
   * useProjectActivityManager will watch and determine if the activities need to be fetched.
   */
  useProjectActivityManager();

  const canUpdateResources = nextDeployment?._links?.["#edit"]?.href;

  const { shouldShowDeploymentType, isManualDeploymentEnabled } =
    useDeploymentType({
      organizationId,
      projectId,
      environmentId
    });

  const updateCpuRamHandler = (
    value: string,
    resourceCollectionType: ResourceCollectionType,
    label: string
  ): void => {
    setChangedResourcesNext(oldResources => {
      const updatedResources = {
        ...oldResources!
      };

      setDeep(
        updatedResources,
        [resourceCollectionType, label, "resources", "profile_size"],
        value
      );

      return updatedResources;
    });
  };
  const updateInstancesRamHandler = (
    value: number,
    resourceCollectionType: ResourceCollectionType,
    label: string
  ): void => {
    setChangedResourcesNext(oldResources => {
      const updatedResources = {
        ...oldResources!
      };

      setDeep(
        updatedResources,
        [resourceCollectionType, label, "instance_count"],
        value
      );

      return updatedResources;
    });
  };
  const updateDiskHandler = (
    newValue: string,
    oldValue: number | null,
    resourceCollectionType: ResourceCollectionType,
    label: string
  ): void => {
    let newNumber = newValue;

    newNumber = isNaN(+newNumber) ? "0" : newNumber;

    if (isTrial) {
      newNumber = `${Math.min(
        +newNumber,
        trialResourceLimits.disk * 1024 -
          totalValues.disk * 1024 +
          (oldValue ?? 0)
      )}`;
    }

    newNumber = `${Math.max(+newNumber, 0)}`;

    setChangedResourcesNext(oldResources => {
      const updatedResources = {
        ...oldResources!
      };

      setDeep(
        updatedResources,
        [resourceCollectionType, label, "disk"],
        newNumber
      );

      return updatedResources;
    });
  };
  const handleDiskBlur = (
    newValue: string,
    oldValue: number | null,
    minDisk: number,
    resourceCollectionType: ResourceCollectionType,
    label: string
  ) => {
    const newNumber = `${Math.max(+newValue, minDisk)}`;

    updateDiskHandler(newNumber, oldValue, resourceCollectionType, label);
  };
  const handleEnvironmentDropdownRedirect = (option: EnvironmentSelection) => {
    const endQuery = projectCreation
      ? QUERY_PROJECT_CREATION
      : projectBilling
        ? QUERY_PROJECT_BILLING
        : "";
    const pathName = location.pathname.replace(environmentId, option.value);

    const search = encodeURIComponent(
      previousPage!.replace(environmentId, option.value)
    );

    let url = pathName + QUERY_PREVIOUS_PAGE + search + endQuery;

    /**
     * When switching from the project overview page to environment overview page update the environmentId for the url and the previous page url
     */
    if (
      environmentId === project?.default_branch &&
      previousPage!.search(environmentId) < 0 &&
      !projectCreation &&
      !projectBilling
    ) {
      url =
        pathName +
        QUERY_PREVIOUS_PAGE +
        previousPage +
        encodeURIComponent(`/${option.value}`);
    }

    navigate(url, { replace: true });
  };

  const checkForIncompleteFields = () => {
    const errors: Record<
      ResourceCollectionType,
      Record<string, { profileSize: boolean; disk: boolean }>
    > = { webapps: {}, services: {}, workers: {} };
    let hasErrors = false;

    objectEntries(changedResourcesNext).forEach(
      ([resourceCollectionType, resourceCollection]) => {
        Object.entries(resourceCollection ?? {}).forEach(([label, data]) => {
          if (Object.keys(data).length) {
            if (!data.resources?.profile_size) {
              setDeep(
                errors,
                [resourceCollectionType, label, "profileSize"],
                true
              );

              hasErrors = true;
            } else {
              setDeep(
                errors,
                [resourceCollectionType, label, "profileSize"],
                false
              );
            }

            const diskIsNumber = typeof data.disk === "string";
            const minDiskIsNumber =
              typeof data.resources?.minimum?.disk === "number";

            if (
              !diskIsNumber &&
              minDiskIsNumber &&
              resourceCollectionType !== "workers"
            ) {
              // A resource that has a minimum disk requirement of "0" shouldn't require a user to input a disk value.
              if (data.resources.minimum?.disk !== 0) {
                setDeep(errors, [resourceCollectionType, label, "disk"], true);

                hasErrors = true;
              }
            } else {
              setDeep(errors, [resourceCollectionType, label, "disk"], false);
            }
          }
        });
      }
    );

    setResourceErrors(errors);
    return hasErrors;
  };

  const addResourceCollectionTypeHandler = (
    resourceData: FormState[ResourceTypes],
    resourceType: ResourceTypes
  ) => {
    for (const key in resourceData) {
      resourceData[key] = {
        ...resourceData[key],
        resourceCollectionType: resourceType
      };
    }

    return resourceData;
  };

  useEffect(() => {
    if (!isDeploymentLoading && nextDeployment) {
      const stateReferenceResources = {
        webapps: addResourceCollectionTypeHandler(
          structuredClone(nextDeployment?.webapps as FormState["webapps"]),
          "webapps"
        ),
        workers: addResourceCollectionTypeHandler(
          structuredClone(nextDeployment?.workers as FormState["workers"]),
          "workers"
        ),
        services: addResourceCollectionTypeHandler(
          structuredClone(nextDeployment?.services as FormState["services"]),
          "services"
        )
      } as LegacyFormStateModifiedFlexibleResources;

      /**
       * The disk input requires the ability to add a "." and therefore the values must be a string.
       * Before saving the disk values they are turned back into numbers
       */
      Object.entries(stateReferenceResources ?? {}).forEach(
        ([label, deploymentServices]) => {
          if (label !== "workers") {
            Object.values(deploymentServices ?? {}).forEach(
              deploymentService => {
                if (typeof deploymentService?.disk === "number") {
                  deploymentService.disk = `${deploymentService.disk}`;
                }
              }
            );
          }
        }
      );

      setChangedResourcesNext(structuredClone(stateReferenceResources));
      setInitialResourcesNext(structuredClone(stateReferenceResources));
    }
  }, [isDeploymentLoading, nextDeployment, resourceStatus]);

  useEffect(() => {
    if (initialResourcesNext && changedResourcesNext && nextDeployment) {
      let totalResources = { cpu: 0, ram: 0, instances: 0, disk: 0 };
      const changesToResources: Change[] = [];

      objectEntries(changedResourcesNext).forEach(
        ([resourceCollectionType, resourceCollection]) => {
          objectEntries(resourceCollection ?? {}).forEach(([label, data]) => {
            const containerProfiles:
              | Record<string, { cpu: number; memory: number }>
              | undefined =
              nextDeployment?.container_profiles[data?.container_profile!];

            const cpu =
              containerProfiles?.[data?.resources?.profile_size!]?.cpu ?? 0;
            const ram =
              containerProfiles?.[data?.resources?.profile_size!]?.memory ?? 0;
            const instances = data.instance_count ?? 1;
            const disk = typeof data.disk === "undefined" ? 0 : +data.disk;

            totalResources = resourcesAccumulatorHandler(totalResources, {
              cpu,
              ram,
              instances,
              disk
            });

            const initialResourcesData =
              initialResourcesNext[resourceCollectionType][label];

            const initialContainerProfile =
              initialResourcesData.container_profile;
            const initialProfileSize =
              initialResourcesData.resources?.profile_size;

            const initialCpu =
              nextDeployment?.container_profiles[initialContainerProfile!]?.[
                initialProfileSize!
              ]?.cpu ?? 0;
            const initialRam =
              nextDeployment?.container_profiles[initialContainerProfile!]?.[
                initialProfileSize!
              ]?.memory ?? 0;
            const initialInstances = initialResourcesData.instance_count ?? 1;
            const initialDisk = initialResourcesData.disk ?? "0";

            const cpuCompare = compareResource(initialCpu, cpu);
            const ramCompare = compareResource(initialRam, ram);
            const instanceCompare = compareResource(
              initialInstances,
              instances
            );
            const diskCompare = compareResource(+initialDisk, +disk);

            if (cpuCompare) {
              changesToResources.push(cpuCompare);
            }
            if (ramCompare) {
              changesToResources.push(ramCompare);
            }
            if (instanceCompare) {
              changesToResources.push(instanceCompare);
            }
            if (diskCompare) {
              changesToResources.push(diskCompare);
            }
          });
        }
      );

      setTotalValues(resourcesFormatHandler(totalResources));

      setHasChangedResources(
        !changesToResources.length
          ? ""
          : changesToResources.find(status => status === "reduced")
            ? "reduced"
            : "increased"
      );

      const hasErrors = checkForIncompleteFields();
      if (!hasErrors) {
        setShowErrors(false);
      }
    }
    // Adding "checkForIncompleteFields" will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changedResourcesNext,
    initialResourcesNext,
    nextDeployment?.container_profiles,
    nextDeployment
  ]);

  const orderedResourcesHandler = () => {
    const [webapps, workers, services] = Object.values(
      changedResourcesNext ?? {}
    );

    const workersTodo = objectEntries(workers);
    const unclaimedWorkers: typeof workersTodo = [];
    const webappsWithWorkers = [...objectEntries(webapps)];
    workersTodo.forEach(worker => {
      const webappLabel = worker[0].split("--")[0];

      let indexToPlaceWorker = -1;
      for (let i = webappsWithWorkers.length - 1; i >= 0; i--) {
        if (webappsWithWorkers[i][0].startsWith(webappLabel)) {
          indexToPlaceWorker = i;
          break;
        }
      }

      if (indexToPlaceWorker > -1) {
        webappsWithWorkers.splice(indexToPlaceWorker + 1, 0, worker);
      } else {
        unclaimedWorkers.push(worker);
      }
    });

    return [
      ...webappsWithWorkers,
      ...unclaimedWorkers,
      ...objectEntries(services)
    ];
  };

  const [isTrial, trialMessageKey] = useMemo(() => {
    const isTrial = getTrial(organizationProfile);

    return [
      isTrial,
      isTrial ? checkTrialMessage(organizationProfile?.current_trial) : null
    ];
  }, [organizationProfile]);

  const trialResourceLimits = {
    cpu: 4.5,
    ram: 12,
    disk: 20,
    project: 1,
    environment: 2
  };

  const isTrialResourcesExceeded =
    isTrial &&
    (totalValues.cpu > trialResourceLimits.cpu ||
      totalValues.ram > trialResourceLimits.ram ||
      totalValues.disk > trialResourceLimits.disk);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const hasErrors = checkForIncompleteFields();
    setShowErrors(hasErrors);

    const differenceBetweenResources = difference(
      initialResourcesNext as ModifiedFlexibleResources,
      changedResourcesNext as ModifiedFlexibleResources
    ) as DeploymentUpdateParams;

    /**
     * The disk input values have been mutated to a string.
     * Before saving the disk values they are turned back into numbers
     */
    Object.entries(differenceBetweenResources ?? {}).forEach(
      ([label, deploymentServices]) => {
        if (label !== "workers") {
          Object.values(deploymentServices ?? {}).forEach(deploymentService => {
            if (typeof deploymentService?.disk === "string") {
              deploymentService.disk = +deploymentService.disk;
            }
          });
        }
      }
    );

    if (nextDeployment && !hasErrors) {
      const result = await dispatch(
        updateDeployment({
          deployment: nextDeployment,
          data: differenceBetweenResources,
          organizationId,
          projectId,
          environmentId
        })
      );

      if (result.type === updateDeployment.fulfilled.type) {
        await dispatch(
          loadDeploymentCurrent({ organizationId, projectId, environmentId })
        );
        await dispatch(
          loadDeploymentNext({
            organizationId,
            projectId,
            environmentId
          })
        );

        notify.success({
          title: intl.formatMessage({
            id: "configure_resources.page.notification.title",
            defaultMessage: "Resources updated and deploying"
          }),
          description: intl.formatMessage(
            {
              id: "configure_resources.page.notification.description"
            },
            {
              strong: content => <strong>{content}</strong>,
              environmentName: defaultEnvironmentSelection?.label
            }
          ),
          link: {
            title: intl.formatMessage(
              {
                id: "configure_resources.page.notification.linkTitle"
              },
              {
                environmentName: defaultEnvironmentSelection?.label
              }
            ),
            location: {
              to: environmentOverviewUrl
            }
          }
        });

        onExit();
      }
    }
  };

  const errorAlert = deploymentNextError?.message && (
    <S.StickerRoot priority="critical">
      <Sticker.Icon priority="critical" />
      <Sticker.Text>{deploymentNextError.message}</Sticker.Text>
    </S.StickerRoot>
  );

  const billingWarning = trialMessageKey === "suspended" && (
    <S.StickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.billing.warning",
          defaultMessage:
            "To configure your project resources you need to add your billing details as your free trial has expired."
        })}
      </Sticker.Text>
    </S.StickerRoot>
  );

  const reduceWarning = hasChangedResources === "reduced" && (
    <S.ReduceStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.reduce.warning"
        })}
      </Sticker.Text>
    </S.ReduceStickerRoot>
  );

  const showNoResourcesWarningMessage =
    (!changedResourcesNextLength ||
      ["Build failed", "No resources configured"].includes(resourceStatus)) &&
    !isEnvironmentLoading &&
    !isDeploymentLoading &&
    !isUpdateActivityInProgress;

  const noResourcesWarning = showNoResourcesWarningMessage && (
    <S.NoResourceStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage(
          {
            id: "configure_resources.page.no_resources.warning"
          },
          {
            link: chucks => (
              <S.Link
                variant="link"
                href={intl.formatMessage({
                  id: "links.documentation.resources.configuration"
                })}
              >
                {chucks}
              </S.Link>
            )
          }
        )}
      </Sticker.Text>
    </S.NoResourceStickerRoot>
  );

  const trialResourcesExceededWarning = isTrialResourcesExceeded && (
    <S.StickerRoot priority="critical">
      <Sticker.Icon priority="critical" />
      <Sticker.Text>
        {intl.formatMessage(
          {
            id: "configure_resources.page.trial_resources.warning"
          },
          trialResourceLimits
        )}
      </Sticker.Text>
    </S.StickerRoot>
  );

  const adminUpdateResourcesWarning = !canUpdateResources && (
    <S.PermissionsStickerRoot priority="information">
      <Sticker.Icon priority="information" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.admin.update.resources.warning",
          defaultMessage:
            "Ask your project admin to update the resources on this environment."
        })}
      </Sticker.Text>
    </S.PermissionsStickerRoot>
  );

  const resourceUpdateInProgressWarning = isUpdateActivityInProgress && (
    <S.PermissionsStickerRoot priority="normal">
      <Sticker.Icon priority="normal" />
      <Sticker.Text>
        {intl.formatMessage({
          id: "configure_resources.page.resources.update.in_progress.warning",
          defaultMessage:
            "A resource update activity is currently in progress. To change resources on this environment, please check back after that activity has completed."
        })}
      </Sticker.Text>
    </S.PermissionsStickerRoot>
  );

  let successBtn = (
    <Button
      analyticId="save"
      type="submit"
      disabled={
        !hasChangedResources || isTrialResourcesExceeded || !canUpdateResources
      }
    >
      {intl.formatMessage({ id: `save`, defaultMessage: "Save" })}
    </Button>
  );

  if (!changedResourcesNextLength) {
    successBtn = (
      <Button onClick={onExit} analyticId="configure_resources.close">
        {intl.formatMessage({
          id: `configure_resources.close`,
          defaultMessage: "Close"
        })}
      </Button>
    );
  } else if (trialMessageKey === "suspended") {
    successBtn = (
      <Button
        to={organizationBillingUrl}
        analyticId="configure_resources.upgrade"
      >
        {intl.formatMessage({
          id: `configure_resources.upgrade`,
          defaultMessage: "Upgrade your account"
        })}
      </Button>
    );
  }

  if (isDeploymentUpdatingLoading) {
    successBtn = <Loading iconOnly={true} />;
  }

  const description = intl.formatMessage(
    {
      id:
        changedResourcesNextLength && resourceStatus !== "Build failed"
          ? "configure_resources.page.text"
          : "configure_resources.page.no_resources.description"
    },
    {
      link: chunks => (
        <Button
          variant="link"
          to={buildResourcesSettingsUrl}
          analyticId="build_resources_settings_url"
        >
          {chunks}
        </Button>
      )
    }
  );

  return (
    <ProjectError>
      <ErrorBoundary>
        <S.FormWrapper onSubmit={handleSubmit}>
          {isDeploymentLoading ||
          isEnvironmentLoading ||
          (typeof currentCode === "undefined" &&
            typeof nextCode === "undefined") ? (
            <S.Loading iconOnly />
          ) : (
            <S.ScrollContainer>
              {isTrial && <SubscribeBanner />}
              <S.VendorIconWrapper>
                <Button to="/">
                  <Logo isFullLogo />
                </Button>
              </S.VendorIconWrapper>
              <S.ContentWrapper>
                <S.ProjectWrapper>
                  <Typography.Label>{project?.title ?? ""}</Typography.Label>
                </S.ProjectWrapper>
                <S.ConfigureResourcesTitle tag="h1">
                  {intl.formatMessage({
                    id: "configure_resources.page.title",
                    defaultMessage: "Configure resources"
                  })}
                </S.ConfigureResourcesTitle>
                {noResourcesWarning}
                <S.ConfigureResourcesDescription>
                  {description}
                </S.ConfigureResourcesDescription>
                <S.EnvironmentSelectWrapper>
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "configure_resources.page.environments",
                      defaultMessage: "Select environment"
                    })}
                  </Typography.Label>
                  <S.EnvironmentLoadingWrapper>
                    <S.Select
                      placeholder={intl.formatMessage({
                        id: "configure_resources.page.environments",
                        defaultMessage: "Select environment"
                      })}
                      value={defaultEnvironmentSelection}
                      options={
                        environmentDropdownOptions ?? [
                          defaultEnvironmentSelection
                        ]
                      }
                      onChange={option => {
                        handleEnvironmentDropdownRedirect(
                          option as EnvironmentSelection
                        );
                      }}
                      isDisabled={environments.length === 1}
                      isMulti={false}
                    />
                    {isUpdateActivityInProgress ? (
                      <Loading iconOnly />
                    ) : (
                      <S.LoadingSpacer />
                    )}
                  </S.EnvironmentLoadingWrapper>
                </S.EnvironmentSelectWrapper>
                {resourceUpdateInProgressWarning}
                <S.PreGridWrapper>
                  <S.TitleAndDialogWrapper>
                    <S.Title tag="h4">
                      {intl.formatMessage({
                        id: "configure_resources.page.container_sizes",
                        defaultMessage: "Container sizes"
                      })}
                    </S.Title>

                    <Dialog.Root>
                      <Dialog.Trigger analyticId="configure_resources.page.dialog.text">
                        <IconControl>
                          <Info
                            width={20}
                            fill="var(--icon-granite-fill,var(--granite))"
                          />
                        </IconControl>
                      </Dialog.Trigger>
                      <Dialog.Content>
                        <Dialog.Title>
                          {intl.formatMessage({
                            id: "configure_resources.page.container_sizes",
                            defaultMessage: "Container sizes"
                          })}
                        </Dialog.Title>
                        <Dialog.Description>
                          {intl.formatMessage({
                            id:
                              shouldShowDeploymentType &&
                              isManualDeploymentEnabled
                                ? "configure_resources.page.dialog.text.manual"
                                : "configure_resources.page.dialog.text",
                            defaultMessage:
                              "Changing your resources will trigger an environment redeploy."
                          })}
                        </Dialog.Description>

                        <Button
                          analyticId="configure_resources.page.dialog.link.text"
                          variant="link"
                          href={intl.formatMessage({
                            id: "links.documentation.resources.configuration"
                          })}
                        >
                          {intl.formatMessage({
                            id: "configure_resources.page.dialog.link.text",
                            defaultMessage: "Documentation"
                          })}
                        </Button>
                      </Dialog.Content>
                    </Dialog.Root>
                  </S.TitleAndDialogWrapper>
                  {hasChangedResources ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setChangedResourcesNext(
                          structuredClone(initialResourcesNext)
                        );
                        setShowErrors(false);
                      }}
                      analyticId="configure_resources.page.clear"
                    >
                      {intl.formatMessage({
                        id: "configure_resources.page.clear",
                        defaultMessage: "Clear changes"
                      })}
                    </Button>
                  ) : (
                    <S.ClearBtnSpacer />
                  )}
                </S.PreGridWrapper>

                <R.ResourceRow
                  autoscalingEnabled={autoscalingEnabledFlag && hasCapability}
                >
                  <Text disabled={true}>
                    {intl.formatMessage({
                      id: `configure_resources.page.apps_n_services`,
                      defaultMessage: "Apps and services"
                    })}
                  </Text>
                  {lessThan720 ? (
                    <div />
                  ) : (
                    <S.TitleAndDialogWrapper>
                      <S.IndentedText disabled={true}>
                        {intl.formatMessage({
                          id: `configure_resources.page.cpu_n_ram`,
                          defaultMessage: "CPU & RAM"
                        })}
                      </S.IndentedText>

                      <Dialog.Root>
                        <Dialog.Trigger analyticId="configure_resources.page.dialog.cpu&ram.description">
                          <IconControl>
                            <Info
                              width={20}
                              fill="var(--icon-granite-fill,var(--granite))"
                            />
                          </IconControl>
                        </Dialog.Trigger>
                        <Dialog.Content>
                          <Dialog.Title>
                            {intl.formatMessage({
                              id: "configure_resources.page.dialog.cpu&ram.title",
                              defaultMessage: "Advanced container profiles"
                            })}
                          </Dialog.Title>
                          <Dialog.Description>
                            {intl.formatMessage({
                              id: "configure_resources.page.dialog.cpu&ram.description",
                              defaultMessage:
                                "You can change the container profile of an application or service in your Yaml configuration file."
                            })}
                          </Dialog.Description>
                          <Button
                            variant="link"
                            href={intl.formatMessage({
                              id: "links.documentation.resources.configuration.advanced_container_profiles"
                            })}
                            analyticId="configure_resources.page.dialog.link.text"
                          >
                            {intl.formatMessage({
                              id: "configure_resources.page.dialog.link.text",
                              defaultMessage: "Documentation"
                            })}
                          </Button>
                        </Dialog.Content>
                      </Dialog.Root>
                    </S.TitleAndDialogWrapper>
                  )}
                  {lessThan720 ? (
                    <div />
                  ) : (
                    <S.TitleAndDialogWrapper>
                      <S.IndentedText disabled={true}>
                        {intl.formatMessage({
                          id: `configure_resources.page.instances`,
                          defaultMessage: "Instances"
                        })}
                      </S.IndentedText>
                      <Dialog.Root>
                        <Dialog.Trigger analyticId="configure_resources.page.dialog.instances.description">
                          <IconControl>
                            <Info
                              width={20}
                              fill="var(--icon-granite-fill,var(--granite))"
                            />
                          </IconControl>
                        </Dialog.Trigger>
                        <Dialog.Content align="end">
                          <Dialog.Title>
                            {intl.formatMessage({
                              id: "configure_resources.page.dialog.instances.title"
                            })}
                          </Dialog.Title>
                          <Dialog.Description>
                            {intl.formatMessage({
                              id: "configure_resources.page.dialog.instances.description"
                            })}
                          </Dialog.Description>
                          <Button
                            variant="link"
                            href={intl.formatMessage({
                              id: "links.documentation.manage_resources.horizontal_scaling"
                            })}
                            analyticId="configure_resources.page.dialog.link.text"
                          >
                            {intl.formatMessage({
                              id: "configure_resources.page.dialog.link.text"
                            })}
                          </Button>
                        </Dialog.Content>
                      </Dialog.Root>
                    </S.TitleAndDialogWrapper>
                  )}
                  {lessThan720 ? (
                    <div />
                  ) : (
                    <S.IndentedText disabled={true}>
                      {intl.formatMessage({
                        id: `configure_resources.page.disk_n_storage`,
                        defaultMessage: "Disk / storage"
                      })}
                    </S.IndentedText>
                  )}
                  {autoscalingEnabledFlag && hasCapability && (
                    <S.IndentedText disabled={true}>
                      {intl.formatMessage({
                        id: "configure.resources.autoscaling",
                        defaultMessage: "Autoscaling"
                      })}
                    </S.IndentedText>
                  )}
                </R.ResourceRow>
                <S.Line />
              </S.ContentWrapper>
              <S.ContentWrapper>
                {changedResourcesNextLength ? (
                  <div>
                    {orderedResourcesHandler()?.map(([label, data]) => {
                      const ContainerProfiles =
                        nextDeployment?.container_profiles[
                          data?.container_profile!
                        ];

                      const instanceLimit = (
                        nextDeployment?.project_info as
                          | {
                              capabilities:
                                | { instance_limit: number }
                                | undefined;
                            }
                          | undefined
                      )?.capabilities?.instance_limit;

                      const minimumResources = data?.resources?.minimum;
                      const minCpu = minimumResources?.cpu ?? 0;
                      const minRam = minimumResources?.memory ?? 0;
                      const minDisk = minimumResources?.disk ?? 0;

                      const defaultSelection = {
                        value: "",
                        label: "",
                        disabled: false
                      };

                      const cupRamGroupLabel = getResourceProfileLabel(
                        data?.container_profile!
                      );

                      const sharedContainerProfiles = Object.fromEntries(
                        Object.entries(ContainerProfiles ?? {}).filter(
                          profileData => !profileData[0].endsWith(".gc")
                        )
                      );

                      const cpuRamOptions = Object.entries(
                        sharedContainerProfiles ?? {}
                      )
                        .sort(([, a], [, b]) => a?.cpu - b?.cpu)
                        .map(([k, v]) => ({
                          disabled:
                            v?.cpu < minCpu ||
                            v?.memory < minRam ||
                            (isTrial &&
                              (totalValues.cpu -
                                (sharedContainerProfiles?.[
                                  data.resources.profile_size ?? ""
                                ]?.cpu ?? 0) *
                                  (data.instance_count ?? 1) +
                                v?.cpu * (data.instance_count ?? 1) >
                                trialResourceLimits.cpu ||
                                totalValues.ram -
                                  (sharedContainerProfiles?.[
                                    data.resources.profile_size ?? ""
                                  ]?.memory ?? 0) *
                                    (data.instance_count ?? 1) +
                                  v?.memory * (data.instance_count ?? 1) >
                                  trialResourceLimits.ram * 1024)),
                          value: k,
                          label: `${v?.cpu} CPU, ${roundedNumber(
                            v?.memory / 1024,
                            3
                          )} GB RAM`
                        }));

                      const cpuRamSelection =
                        cpuRamOptions.find(
                          option =>
                            option.value === data?.resources?.profile_size
                        ) ?? defaultSelection;

                      const instancesRange = new Array(instanceLimit ?? 0)
                        .fill(0)
                        .map((_, index) => index + 1);
                      const instancesOptions = instancesRange.map(c => ({
                        value: `${c}`,
                        label: `${c}`,
                        disabled:
                          isTrial &&
                          (totalValues.cpu -
                            (sharedContainerProfiles?.[
                              data.resources.profile_size ?? ""
                            ]?.cpu ?? 0) *
                              (data.instance_count ?? 1) +
                            (sharedContainerProfiles?.[
                              data.resources.profile_size ?? ""
                            ]?.cpu ?? 0) *
                              c >
                            trialResourceLimits.cpu ||
                            totalValues.ram -
                              (sharedContainerProfiles?.[
                                data.resources.profile_size ?? ""
                              ]?.memory ?? 0) *
                                (data.instance_count ?? 1) +
                              (sharedContainerProfiles?.[
                                data.resources.profile_size ?? ""
                              ]?.memory ?? 0) *
                                c >
                              trialResourceLimits.ram * 1024)
                      }));

                      const instancesSelection =
                        instancesOptions.find(
                          option =>
                            +option.value === (data?.instance_count ?? 1)
                        ) ?? defaultSelection;

                      const minDiskIsNumber =
                        typeof data.resources?.minimum?.disk === "number";

                      const diskOnlyText = () => {
                        if (data.resourceCollectionType === "workers") {
                          return "App storage";
                        } else if (!minDiskIsNumber) {
                          return "0";
                        }
                        return "";
                      };

                      const diskIsSet =
                        typeof initialResourcesNext![
                          data.resourceCollectionType
                        ][label]?.disk === "number";

                      const diskTooltip = () => {
                        if (!minDiskIsNumber) {
                          return "";
                        }
                        if (data.resources?.minimum?.disk === 0) {
                          return intl.formatMessage({
                            id: "configure_resources.page.disk.services.tooltip.no.minimum"
                          });
                        }

                        return intl.formatMessage(
                          {
                            id: "configure_resources.page.disk.services.tooltip"
                          },
                          {
                            minDisk: (data.resources?.minimum?.disk || 0) / 1024
                          }
                        );
                      };
                      return (
                        <React.Fragment key={`${label}:${data?.type}`}>
                          <ResourceRow
                            service={data as ModifiedDeploymentService}
                            alt={data?.type}
                            title={label}
                            isWorker={data.resourceCollectionType === "workers"}
                            formIsDisabled={
                              !canUpdateResources || isUpdateActivityInProgress
                            }
                            // CPU & RAM
                            cpuRamOptions={[
                              {
                                label: cupRamGroupLabel,
                                options: cpuRamOptions
                              }
                            ]}
                            cpuRamValue={cpuRamSelection}
                            cpuRamOnChange={option => {
                              updateCpuRamHandler(
                                option.value,
                                data.resourceCollectionType,
                                label
                              );
                            }}
                            cpuRamError={
                              showErrors &&
                              resourceErrors?.[data.resourceCollectionType][
                                label
                              ]?.profileSize
                            }
                            // Instance
                            instancesOptions={instancesOptions}
                            instancesValue={instancesSelection}
                            instancesOnChange={option => {
                              updateInstancesRamHandler(
                                +option.value,
                                data.resourceCollectionType,
                                label
                              );
                            }}
                            instancesOnlyText={
                              data.resourceCollectionType === "services"
                                ? "1"
                                : ""
                            }
                            // Disk
                            diskValue={
                              (data?.disk as string | undefined) ?? null
                            }
                            onDiskBlur={(value, diskValue) => {
                              handleDiskBlur(
                                value,
                                diskValue,
                                minDisk,
                                data.resourceCollectionType,
                                label
                              );
                            }}
                            diskOnChange={(value, diskValue) => {
                              updateDiskHandler(
                                value,
                                diskValue,
                                data.resourceCollectionType,
                                label
                              );
                            }}
                            diskOnlyText={diskOnlyText()}
                            diskError={
                              (showErrors &&
                                resourceErrors?.[data.resourceCollectionType][
                                  label
                                ]?.disk) ??
                              false
                            }
                            diskTooltip={diskTooltip()}
                            showTooltip={!diskIsSet}
                          />
                          <S.Line hideLast={true} />
                        </React.Fragment>
                      );
                    })}
                    <Total
                      cpu={totalValues.cpu}
                      ram={totalValues.ram}
                      instances={totalValues.instances}
                      disk={+totalValues.disk.toFixed(2)}
                    />
                    {adminUpdateResourcesWarning}
                    {isEnvironmentInactive && <InactiveEnvironmentWarning />}
                  </div>
                ) : (
                  <S.NoResourcesText weight="semiBold">
                    {intl.formatMessage({
                      id: "configure_resources.page.no_resources.note",
                      defaultMessage: "No apps or services are configured."
                    })}
                  </S.NoResourcesText>
                )}
              </S.ContentWrapper>
              <S.Footer>
                {billingWarning}
                {reduceWarning}
                {trialResourcesExceededWarning}
                {errorAlert}
                <S.Label>
                  {isTrial
                    ? intl.formatMessage({
                        id: "configure_resources.page.note.trial"
                      })
                    : intl.formatMessage(
                        { id: "configure_resources.page.note" },
                        {
                          link: chunks => (
                            <S.PricingButton
                              variant="link"
                              analyticId="links.pricing"
                              href={intl.formatMessage({
                                id: "links.pricing"
                              })}
                            >
                              {chunks}
                            </S.PricingButton>
                          )
                        }
                      )}
                </S.Label>
                <S.ButtonWrapper spacing="modal">
                  <Button
                    variant="secondary"
                    onClick={onExit}
                    analyticId="cancel"
                  >
                    {intl.formatMessage({
                      id: "cancel",
                      defaultMessage: "Cancel"
                    })}
                  </Button>
                  {successBtn}
                </S.ButtonWrapper>
              </S.Footer>
            </S.ScrollContainer>
          )}
        </S.FormWrapper>
      </ErrorBoundary>
    </ProjectError>
  );
};

export default ConfigureResourcesPage;
